const headersTextBase = {
  color: 'var(--chakra-colors-text-secondary-light)',
  textTransform: 'none',
  textStyle: 'caption',
  fontWeight: '500',
  fontSize: '0.75rem',
  flex: '1',
};

const basePadding = {
  x: '1.125rem',
  y: '1rem',
};

export const DataTable = {
  parts: [
    'container',
    'header',
    'tableContainer',
    'search',
    'searchInputGroup',
    'searchInput',
    'filters',
    'slider',
    'headers',
    'headersText',
    'tableMenuContainer',
    'tableMenuDropdownCell',
    'tableMenuDropdownMenuItem',
    'tableMenuDropdownMenuItemOption',
    'dataRows',
    'dataRowsText',
    'footer',
    'footerText',
  ],
  baseStyle: {
    container: { bg: 'transparent' },
    header: { mb: '1rem' },
    tableContainer: {
      width: '100%',
      borderRadius: 'var(--card-radius)',
      bg: 'var(--chakra-colors-background-container-light)',
      _dark: {
        bg: 'var(--chakra-colors-background-container-dark)',
      },
    },
    search: {
      marginBottom: '1rem',
      marginTop: '0',
      width: '100%',
      maxWidth: '595px',
      height: '37px',
    },
    searchInputGroup: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      height: '35px',
      width: '100%',
      border: '1px solid var(--chakra-colors-border-form-default-light)',
      borderRadius: 'base',
      backgroundColor: 'var(--chakra-colors-background-form-default-light)',
    },
    searchInput: {
      height: '35px',
      textStyle: 'body',
      outlineColor: 'transparent',
      color: 'var(--chakra-colors-text-secondary-light)',
    },
    filters: {
      flex: 1,
      textAlign: 'right',
      alignItems: 'center',
      marginBottom: '1rem',
      marginLeft: '1rem',
    },
    headers: {
      px: basePadding.x,
      py: '0.875rem',
      borderColor: 'var(--chakra-colors-border-opaque-light)',
      letterSpacing: 'normal',
      _dark: {
        borderColor: 'var(--chakra-colors-border-opaque-dark)',
      },
    },
    headersText: {
      ...headersTextBase,
    },
    sortableHeadersText: {
      ...headersTextBase,
      cursor: 'pointer',
    },
    tableMenuDropdownCell: {
      px: '0',
      width: '.75rem',
      height: '.75rem',
      borderColor: 'var(--chakra-colors-border-opaque-light)',
      color: 'var(--chakra-colors-text-secondary-light)',
      svg: {
        color: 'var(--chakra-colors-text-secondary-light)',
        stroke: 'var(--chakra-colors-text-secondary-light)',
      },
      _dark: {
        borderColor: 'var(--chakra-colors-border-opaque-dark)',
      },
    },
    bulkSelectCell: {
      px: '0',
      borderColor: 'var(--chakra-colors-border-opaque-light)',
      color: 'var(--chakra-colors-text-secondary-light)',
      textAlign: 'center',
      width: '.6rem',
      label: {
        ml: '1rem',
        mt: '3px',
      },
      _dark: {
        borderColor: 'var(--chakra-colors-border-opaque-dark)',
      },
    },
    tableMenuContainer: {
      lineHeight: '1em',
      textAlign: 'center',
      display: 'block',
      textStyle: 'caption',
    },
    tableMenuDropdownMenuItem: {
      svg: {
        width: '1.25rem',
        height: '1.25rem',
      },
    },
    tableMenuDropdownMenuItemOption: {
      ml: 2,
    },
    tableRow: {
      '&:hover': {
        backgroundColor: 'var(--chakra-colors-interactive-hover-light)',
      },
      _dark: {
        '&:hover': {
          backgroundColor: 'var(--chakra-colors-interactive-hover-dark)',
        },
      },
    },
    tableRowHighlight: {
      backgroundColor: 'rgba(220, 234, 255, 0.41)',
    },
    dataRows: {
      px: basePadding.x,
      py: basePadding.y,
      borderColor: 'var(--chakra-colors-border-opaque-light)',
      textStyle: 'body',
      _dark: {
        borderColor: 'var(--chakra-colors-border-opaque-dark)',
      },
    },
    dataRowsText: {
      color: 'var(--chakra-colors-text-base-light)',
      textStyle: 'body',
    },
    dataRowsLabelText: {
      color: 'var(--chakra-colors-text-base-light)',
      textStyle: 'body',
      fontWeight: '500',
    },
    footer: {
      bg: 'var(--chakra-colors-background-nestedContainer-light)',
      borderColor: 'var(--chakra-colors-border-opaque-light)',
      color: 'var(--chakra-colors-text-secondary-light)',
      captionSide: 'bottom',
      borderBottomLeftRadius: 'var(--card-radius)',
      borderBottomRightRadius: 'var(--card-radius)',
      textAlign: 'left',
      px: basePadding.x,
      py: basePadding.y,
      marginTop: '0',
      _dark: {
        bg: 'var(--chakra-colors-background-nestedContainer-dark)',
        borderColor: 'var(--chakra-colors-border-opaque-dark)',
      },
    },
    footerText: {
      color: 'var(--chakra-colors-text-secondary-light)',
      textStyle: 'caption',
      fontSize: '0.75rem',
    },
    footerBulkActionsButton: {
      color: 'text.hyperlink.light',
      fontSize: '0.75rem',
      fontWeight: '500',
      _dark: {
        color: 'text.hyperlink.dark',
      },
    },
  },
  variants: {
    standard: {},
    inline: {},
    'fixed-column': {
      tableRowSolid: {
        /**
         * targets Td because sticky cells must have solid backgrounds,
         * so changing row background wouldn't be visible through a solid cell
         */
        '&:hover': {
          Td: {
            backgroundColor: 'var(--chakra-colors-grey-50)',
          },
        },
      },
      tableRowHighlightSolid: {
        Td: {
          /**
           * solid variant of rgba(220, 234, 255, 0.41) or
           * chakra-colors-accent-blue-bg-light with transparency
           * must be solid because of the sticky columns
           */
          backgroundColor: '#f1f6ff',
        },
      },
      columnFixedFirst: {
        position: 'sticky !important',
        left: 0,
        zIndex: 1,
        minWidth: '22rem',
        width: '22rem',
        backgroundColor: 'var(--chakra-colors-background-container-light)',

        /**
         * added as a border replacement since the regular border
         * on a sticky cell disappears on horizontal scroll
         */
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '0',
          right: '0',
          display: 'block',
          width: '0',
          height: '100%',
          borderRight: '1px solid var(--chakra-colors-gray-200)',
        },
      },
      columnFixedLast: {
        position: 'sticky !important',
        right: '3.5rem',
        zIndex: 1,
        minWidth: '11.25rem',
        width: '11.25rem',
        backgroundColor: 'var(--chakra-colors-background-container-light)',

        /**
         * added as a border replacement since the regular border
         * on a sticky cell disappears on horizontal scroll
         */
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '0',
          left: '0',
          display: 'block',
          width: '0',
          height: '100%',
          borderLeft: '1px solid var(--chakra-colors-gray-200)',
        },
      },
      tableMenuDropdownCellFixed: {
        position: 'sticky !important',
        right: 0,
        zIndex: 1,
        backgroundColor: 'var(--chakra-colors-background-container-light)',
      },
    },
  },
};
